import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PAGE_POSITIONS } from 'types';
import type { AffiliateStockListing, CapiProductPage } from 'types/capi/pdp';

import HiddenIcon from '~/assets/icons/Hidden';
import Modals from '~/components/Modals';
import Tracked from '~/components/Tracked';
import RegisterIntentEvent from '~/events/RegisterIntentEvent';
import usePageData from '~/hooks/usePageData';
import { useRegistrationStart } from '~/hooks/useRegistrationInteraction';
import useRootData from '~/hooks/useRootData';
import { getStockStatusForSize } from '~/utils/listings';

import AffiliateSizes from './AffiliateSizes';
import ShippingAndPrice from './ShippingAndPrice';
import { StockStatus } from './StockListing';

type Props = {
  listing: AffiliateStockListing;
  pagePosition: PAGE_POSITIONS;
  selectedSize?: string;
};

export default function StockListingGated({
  listing,
  selectedSize,
  pagePosition
}: Props) {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const product = usePageData<CapiProductPage>();
  const { t } = useTranslation('pdp');
  const { isBot } = useRootData();
  const recordRegistrationStart = useRegistrationStart();

  const stockStatus = getStockStatusForSize(listing, selectedSize);

  async function handleClick(e: React.SyntheticEvent) {
    e.preventDefault();
    setShowLoginModal(true);
    recordRegistrationStart({ placement: 'coming_soon_gate' });

    return new RegisterIntentEvent({
      buttonName: 'GATED_RETAILER_CLICK',
      isBot,
      pagePosition
    }).send();
  }

  return (
    <div className="flex size-full flex-col justify-center gap-4 rounded-lg bg-white p-4">
      <div className="flex w-full items-center gap-4 bg-white">
        <div className="rounded-lg bg-grey-tile p-2">
          <HiddenIcon />
        </div>
        <StockStatus
          stockStatus={stockStatus}
          listing={listing}
          selectedSize={selectedSize}
        />
        <div className="flex grow justify-end gap-4">
          <ShippingAndPrice listing={listing} selectedSize={selectedSize} />
        </div>
      </div>
      <Tracked.Button
        name="GATED_RETAILER_CLICK"
        pagePosition={pagePosition}
        variant="forest"
        onClick={handleClick}
      >
        {t('gatedRetailerButton')}
      </Tracked.Button>
      <AffiliateSizes selectedSize={selectedSize} sizes={listing.sizes} />
      {showLoginModal && (
        <Modals.SignUp
          modalType="pdpGate"
          registrationEntry={{ placement: 'coming_soon_gate' }}
          hideAppDownloadPrompt
          onDismiss={() => setShowLoginModal(false)}
          image={product.gallery[0]}
        />
      )}
    </div>
  );
}
